import React, { useMemo, useState } from 'react';
import { usePos } from '../context/PosContext';
import { Customer } from '../types';
import {
  SmsSettings,
  SmsTemplate,
  SmsLogEntry,
  getSmsSettings,
  saveSmsSettings,
  getSmsTemplates,
  saveSmsTemplates,
  getSmsLog,
  saveSmsLog,
  smsSegments,
  renderTemplate,
  smsDeepLink,
  smsableCustomers,
} from '../services/sms';
import * as db from '../services/db';
import { formatDateTime } from '../services/datetime';
import './Sections.css';
import './SmsConfig.css';

// SMS Configuration — local-first reconstruction of the Android SMS module:
// gateway/account config + credit balance, message templates ("groups"),
// a send-to-customers campaign flow, and a delivered-SMS log. With no gateway
// in the web port, sending records each rendered message to the local log and
// decrements credits (an honest outbox); single sends also offer an `sms:`
// deep link to hand off to the device's native messaging app.

type Tab = 'gateway' | 'templates' | 'send' | 'log';
const TABS: { key: Tab; label: string; icon: string }[] = [
  { key: 'gateway', label: 'Gateway', icon: '🔑' },
  { key: 'templates', label: 'Templates', icon: '📝' },
  { key: 'send', label: 'Send SMS', icon: '📨' },
  { key: 'log', label: 'Delivery Log', icon: '📋' },
];

const SmsConfig: React.FC = () => {
  const { customers, config } = usePos();
  const [tab, setTab] = useState<Tab>('gateway');

  const [settings, setSettings] = useState<SmsSettings>(() => getSmsSettings());
  const [templates, setTemplates] = useState<SmsTemplate[]>(() => getSmsTemplates());
  const [log, setLog] = useState<SmsLogEntry[]>(() => getSmsLog());

  const persistSettings = (s: SmsSettings) => { saveSmsSettings(s); setSettings(s); };
  const persistTemplates = (t: SmsTemplate[]) => { saveSmsTemplates(t); setTemplates(t); };
  const persistLog = (l: SmsLogEntry[]) => { saveSmsLog(l); setLog(l); };

  return (
    <div className="sms-page section-page">
      <div className="sms-head">
        <div>
          <h1 className="sms-title">SMS Configuration</h1>
          <p className="muted">Gateway account, message templates &amp; customer SMS.</p>
        </div>
        <div className="sms-credits">
          <span className="sms-credits-num">{settings.creditsRemaining}</span>
          <span className="sms-credits-lbl">credits left</span>
        </div>
      </div>

      <div className="sms-tabs">
        {TABS.map((t) => (
          <button key={t.key} className={`sms-tab ${tab === t.key ? 'sel' : ''}`} onClick={() => setTab(t.key)}>
            <span>{t.icon}</span> {t.label}
            {t.key === 'log' && log.length > 0 && <span className="sms-tab-badge">{log.length}</span>}
          </button>
        ))}
      </div>

      {tab === 'gateway' && <GatewayTab settings={settings} onSave={persistSettings} />}
      {tab === 'templates' && <TemplatesTab templates={templates} onChange={persistTemplates} />}
      {tab === 'send' && (
        <SendTab
          customers={customers}
          templates={templates}
          settings={settings}
          storeName={config.storeName}
          onSent={(entries) => {
            persistLog([...log, ...entries]);
            persistSettings({ ...settings, creditsRemaining: Math.max(0, settings.creditsRemaining - entries.filter((e) => e.status === 'sent').length) });
            db.logEvent({ type: 'sms', detail: `Sent ${entries.length} SMS` });
            setTab('log');
          }}
        />
      )}
      {tab === 'log' && <LogTab log={log} onClear={() => persistLog([])} />}
    </div>
  );
};

// ---- Gateway tab --------------------------------------------------------

const GatewayTab: React.FC<{ settings: SmsSettings; onSave: (s: SmsSettings) => void }> = ({ settings, onSave }) => {
  const [f, setF] = useState<SmsSettings>(settings);
  const [saved, setSaved] = useState(false);
  const set = (k: keyof SmsSettings, v: string | number) => { setF((p) => ({ ...p, [k]: v })); setSaved(false); };

  const save = () => { onSave(f); setSaved(true); setTimeout(() => setSaved(false), 1500); };

  return (
    <div className="section-card">
      <h2>Gateway Account</h2>
      <div className="sms-form-grid">
        <label>Provider / Gateway
          <input value={f.provider} onChange={(e) => set('provider', e.target.value)} placeholder="e.g. MSG91, TextLocal" />
        </label>
        <label>Sender ID (DLT header)
          <input value={f.senderId} maxLength={6} onChange={(e) => set('senderId', e.target.value.toUpperCase())} placeholder="6 chars e.g. SCONCT" />
        </label>
        <label className="sms-span2">Auth Key / API Key
          <input value={f.authKey} onChange={(e) => set('authKey', e.target.value)} placeholder="Gateway API key" />
        </label>
        <label>Shop Name
          <input value={f.shopName} onChange={(e) => set('shopName', e.target.value)} />
        </label>
        <label>Contact Name
          <input value={f.contactName} onChange={(e) => set('contactName', e.target.value)} />
        </label>
        <label>Contact Mobile
          <input value={f.contactMobile} maxLength={10} onChange={(e) => set('contactMobile', e.target.value)} />
        </label>
        <label>Email
          <input value={f.email} onChange={(e) => set('email', e.target.value)} />
        </label>
        <label>Credits / Messages Available
          <input type="number" value={f.creditsRemaining} onChange={(e) => set('creditsRemaining', parseInt(e.target.value) || 0)} />
        </label>
      </div>
      <div className="sms-form-actions">
        <button className="btn-teal" onClick={save}>{saved ? '✓ Saved' : 'Save Gateway Settings'}</button>
      </div>
      <p className="muted sms-note">
        No SMS leaves this device without a real gateway — the web port records sent messages to the Delivery Log.
        Configure your DLT-registered provider here to keep credits and sender ID on file.
      </p>
    </div>
  );
};

// ---- Templates tab ------------------------------------------------------

const blankTpl = { name: '', body: '' };

const TemplatesTab: React.FC<{ templates: SmsTemplate[]; onChange: (t: SmsTemplate[]) => void }> = ({ templates, onChange }) => {
  const [editing, setEditing] = useState<SmsTemplate | null>(null);
  const [f, setF] = useState(blankTpl);

  const reset = () => { setEditing(null); setF(blankTpl); };
  const edit = (t: SmsTemplate) => { setEditing(t); setF({ name: t.name, body: t.body }); };

  const save = () => {
    if (!f.name.trim()) return alert('Template name is required.');
    if (!f.body.trim()) return alert('Message body is required.');
    if (editing) onChange(templates.map((t) => (t.id === editing.id ? { ...editing, ...f } : t)));
    else onChange([...templates, { id: db.newId(), ...f }]);
    reset();
  };
  const remove = (t: SmsTemplate) => {
    if (window.confirm(`Delete template "${t.name}"?`)) {
      onChange(templates.filter((x) => x.id !== t.id));
      if (editing?.id === t.id) reset();
    }
  };

  const len = f.body.length;
  const segs = smsSegments(len);

  return (
    <div className="sms-two-pane">
      <div className="section-card sms-tpl-list">
        <h2>Message Templates</h2>
        {templates.length === 0 && <p className="muted">No templates yet.</p>}
        {templates.map((t) => (
          <div key={t.id} className={`sms-tpl-item ${editing?.id === t.id ? 'sel' : ''}`} onClick={() => edit(t)}>
            <div className="sms-tpl-name">{t.name}</div>
            <div className="sms-tpl-body muted">{t.body}</div>
            <button className="row-del sms-tpl-del" onClick={(e) => { e.stopPropagation(); remove(t); }}>✕</button>
          </div>
        ))}
      </div>

      <div className="section-card sms-tpl-form">
        <h2>{editing ? 'Edit Template' : 'New Template'}</h2>
        <label className="sms-lbl">Template Name
          <input value={f.name} onChange={(e) => setF((p) => ({ ...p, name: e.target.value }))} placeholder="e.g. Festival Greeting" />
        </label>
        <label className="sms-lbl">Message Body
          <textarea
            className="sms-body"
            value={f.body}
            maxLength={480}
            onChange={(e) => setF((p) => ({ ...p, body: e.target.value }))}
            placeholder="Dear {name}, ... — {store}"
          />
        </label>
        <div className="sms-body-meta">
          <span>{len} chars · {segs} SMS</span>
          <span className="muted">Placeholders: {'{name} {store} {amount} {bill} {date}'}</span>
        </div>
        <div className="sms-form-actions">
          {editing && <button className="btn-grey" onClick={reset}>New</button>}
          <button className="btn-teal" onClick={save}>{editing ? 'Update Template' : 'Save Template'}</button>
        </div>
      </div>
    </div>
  );
};

// ---- Send tab -----------------------------------------------------------

const SendTab: React.FC<{
  customers: Customer[];
  templates: SmsTemplate[];
  settings: SmsSettings;
  storeName: string;
  onSent: (entries: SmsLogEntry[]) => void;
}> = ({ customers, templates, settings, storeName, onSent }) => {
  const reachable = useMemo(() => smsableCustomers(customers), [customers]);
  const [templateId, setTemplateId] = useState<string>(templates[0]?.id ?? '');
  const [customBody, setCustomBody] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const template = templates.find((t) => t.id === templateId) ?? null;
  const body = customBody || template?.body || '';

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return reachable.filter((c) => !q || c.name.toLowerCase().includes(q) || (c.phone ?? '').includes(q));
  }, [reachable, search]);

  const allVisibleSelected = visible.length > 0 && visible.every((c) => selected.has(c.id));
  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visible.forEach((c) => next.delete(c.id));
      else visible.forEach((c) => next.add(c.id));
      return next;
    });
  };
  const toggle = (id: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  const recipients = reachable.filter((c) => selected.has(c.id));
  const segsPer = smsSegments(body.length);
  const creditsNeeded = recipients.length * segsPer;
  const overBudget = creditsNeeded > settings.creditsRemaining;

  const render = (c: Customer) =>
    renderTemplate(body, { name: c.name, store: storeName, amount: '', bill: '' });

  const send = () => {
    if (!body.trim()) return alert('Pick a template or type a message.');
    if (recipients.length === 0) return alert('Select at least one customer.');
    if (overBudget && !window.confirm(`This needs ${creditsNeeded} credits but only ${settings.creditsRemaining} remain. Record anyway?`)) return;
    const now = new Date().toISOString();
    const entries: SmsLogEntry[] = recipients.map((c) => ({
      id: db.newId(),
      toName: c.name,
      toNumber: c.phone ?? '',
      body: render(c),
      templateName: template?.name,
      status: 'sent',
      timestamp: now,
    }));
    onSent(entries);
    setSelected(new Set());
  };

  // Single-recipient device hand-off (real sms: link).
  const openInApp = (c: Customer) => {
    window.location.href = smsDeepLink(c.phone ?? '', render(c));
  };

  return (
    <div className="sms-two-pane">
      <div className="section-card">
        <h2>Compose</h2>
        <label className="sms-lbl">Template
          <select value={templateId} onChange={(e) => { setTemplateId(e.target.value); setCustomBody(''); }}>
            {templates.length === 0 && <option value="">No templates — add one first</option>}
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </label>
        <label className="sms-lbl">Message {customBody ? '(custom override)' : '(from template — edit to override)'}
          <textarea
            className="sms-body"
            value={body}
            maxLength={480}
            onChange={(e) => setCustomBody(e.target.value)}
            placeholder="Type a message or pick a template…"
          />
        </label>
        <div className="sms-body-meta">
          <span>{body.length} chars · {segsPer} SMS / recipient</span>
        </div>

        <div className={`sms-budget ${overBudget ? 'over' : ''}`}>
          <div><span className="muted">Recipients</span><strong>{recipients.length}</strong></div>
          <div><span className="muted">Credits needed</span><strong>{creditsNeeded}</strong></div>
          <div><span className="muted">Credits left</span><strong>{settings.creditsRemaining}</strong></div>
        </div>
        <button className="btn-teal sms-send-btn" onClick={send} disabled={recipients.length === 0 || !body.trim()}>
          📨 Send to {recipients.length} customer{recipients.length === 1 ? '' : 's'}
        </button>
      </div>

      <div className="section-card sms-recipients">
        <div className="sms-recipients-head">
          <h2>Customer List</h2>
          <label className="sms-selall">
            <input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} /> Select All
          </label>
        </div>
        <input className="sms-search" placeholder="Search name / number…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <div className="sms-cust-scroll">
          {reachable.length === 0 && <p className="muted">No customers have a mobile number. Add numbers in Customers.</p>}
          {visible.map((c) => (
            <div key={c.id} className={`sms-cust-row ${selected.has(c.id) ? 'sel' : ''}`}>
              <label className="sms-cust-main">
                <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} />
                <span className="sms-cust-name">{c.name}</span>
                <span className="muted sms-cust-phone">{c.phone}</span>
              </label>
              <button className="sms-app-btn" title="Open in phone's SMS app" onClick={() => openInApp(c)}>↗ App</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---- Log tab ------------------------------------------------------------

const LogTab: React.FC<{ log: SmsLogEntry[]; onClear: () => void }> = ({ log, onClear }) => {
  const rows = [...log].reverse();

  const exportCsv = () => {
    const head = 'Time,Name,Number,Status,Template,Message\n';
    const esc = (s: string) => `"${(s ?? '').replace(/"/g, '""')}"`;
    const body = rows
      .map((e) => [formatDateTime(e.timestamp), e.toName, e.toNumber, e.status, e.templateName ?? '', e.body].map(esc).join(','))
      .join('\n');
    const blob = new Blob([head + body], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sms-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="section-card">
      <div className="sms-log-head">
        <h2>Delivered SMS List</h2>
        <div className="sms-log-actions">
          <button className="btn-grey" onClick={exportCsv} disabled={rows.length === 0}>Export CSV</button>
          <button className="btn-red" onClick={() => rows.length && window.confirm('Clear the entire SMS log?') && onClear()} disabled={rows.length === 0}>Clear Log</button>
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="muted">No messages sent yet.</p>
      ) : (
        <table className="section-table">
          <thead>
            <tr><th>Time</th><th>To</th><th>Number</th><th>Status</th><th>Message</th></tr>
          </thead>
          <tbody>
            {rows.map((e) => (
              <tr key={e.id}>
                <td className="sms-nowrap">{formatDateTime(e.timestamp)}</td>
                <td>{e.toName}</td>
                <td>{e.toNumber}</td>
                <td><span className={`badge sms-st-${e.status}`}>{e.status}</span></td>
                <td className="sms-msg-cell">{e.body}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default SmsConfig;
